/**
 * The expense wire shapes — `Expense`, `ExpenseCreate`, `ExpenseUpdate`, `BexioBillRef` from
 * `docs/api/paths/expenses.yaml` — and the row type the table and the form render them as.
 * Pure: safe to import from client components.
 */

import type { BexioBill } from '@/lib/api/bexio/types'
import type { ApiAccount } from '@/lib/api/ledger/types'
import { EntryStatusType, EntryStatusTypeValue, Page, strEnum } from '@/lib/api/types'
import { TableFilters, urlEnum, urlIds } from '@/lib/table-state'
import { z } from 'zod'

export const ExpenseType = strEnum([
  'expense_training', // Weiterbildung
  'expense_bill', // Rechnung (Mit Bexio)
  'expense_bill_intern', // Rechung intern (Ohne Bexio)
  'expense_bill_intern_flatrate', // Pauschalspesen
  'expense_business_meal', // Geschäftsessen
  'expense_invoice_meal', // Verpflegungsspesen aus Leistung
])
export type ExpenseTypeValue = keyof typeof ExpenseType

export const ExpenseRecurrenceType = strEnum(['once', 'monthly'])
export type ExpenseRecurrenceTypeValue = keyof typeof ExpenseRecurrenceType

/** `ExpenseBexioStatus`: what the Bexio badge of an expense shows (the v4 bill `status`, https://docs.bexio.com/#tag/Bills). */
export const ExpenseBexioStatus = strEnum([
  'draft',
  'booked',
  'partially_created',
  'created',
  'partially_sent',
  'sent',
  'partially_downloaded',
  'downloaded',
  'partially_paid',
  'paid',
  'partially_failed',
  'failed',
  'unsynced',
  'unknown',
])
export type ExpenseBexioStatusValue = keyof typeof ExpenseBexioStatus

/** The form fields of `ApiBexioBillRef`; the records next to the ids pass through unchecked. */
export const BexioBillSchema = {
  payerContactId: z.string().optional(),
  payer: z.any().optional(),
  accountantUserId: z.string().optional(),
  accountantUser: z.any().optional(),
  accountId: z.string().optional(),
  account: z.any().optional(),
  taxId: z.string().optional(),
  tax: z.any().optional(),
}

/** How an external expense is booked in Bexio: the four ids, next to the records they were picked from. */
export type ApiBexioBillRef = {
  payerContactId?: string | null
  payer?: Record<string, unknown> | null
  accountantUserId?: string | null
  accountantUser?: Record<string, unknown> | null
  accountId?: string | null
  account?: Record<string, unknown> | null
  taxId?: string | null
  tax?: Record<string, unknown> | null
}

export type ApiExpenseWritable = {
  name: string
  description?: string | null
  /** `Money`: fixed two-decimal strings. */
  net: string
  gross: string
  vat: string
  url?: string | null
  billFree: boolean
  type: ExpenseTypeValue
  recurrence: ExpenseRecurrenceTypeValue
  /** The calendar day, `YYYY-MM-DD` (`toApiDay`). */
  issued: string
  sponsor: number
  payer: number
  bexio?: ApiBexioBillRef | null
}

export type ApiExpense = ApiExpenseWritable & {
  id: number
  status: EntryStatusTypeValue
  createdBy: number
  parentId: number | null
  integrationId: string | null
  fileIntegrationId: string | null
  bexioBill: BexioBill | null
  created: string
  updated: string
}

/** `POST /expenses`. */
export type ExpenseCreate = ApiExpenseWritable

/** `PATCH /expenses/{id}` — every field optional. */
export type ExpenseUpdate = Partial<ApiExpenseWritable>

/** An expense as the table and the form render it: dates parsed, both accounts resolved. */
export type ExpenseRecord = Omit<ApiExpense, 'issued' | 'created' | 'updated'> & {
  issued: Date
  created: Date
  updated: Date
}

export type ExpenseRow = {
  expense: ExpenseRecord
  sponsor?: ApiAccount
  payer?: ApiAccount
}

export const toExpenseRow = (expense: ApiExpense, accounts?: Map<number, ApiAccount>): ExpenseRow => ({
  expense: {
    ...expense,
    issued: new Date(expense.issued),
    created: new Date(expense.created),
    updated: new Date(expense.updated),
  },
  sponsor: accounts?.get(expense.sponsor),
  payer: accounts?.get(expense.payer),
})

/** `ExpensePage`: one page of expenses, with the sum of `gross` over every filtered row (#843). */
export type ExpensePage = Page<ApiExpense> & { totals: { gross: string } }

/** The filters of `GET /expenses`. */
export type ExpensesQuery = {
  year?: number
  status?: EntryStatusTypeValue[]
  types?: ExpenseTypeValue[]
  sponsors?: number[]
  payers?: number[]
  bexioStatuses?: ExpenseBexioStatusValue[]
  bexioAccountIds?: number[]
  bexioTaxIds?: number[]
  createdBy?: number
  q?: string
}

/** `ExpensesQuery` as the contract's query params (design.md §8.2). */
export const expensesParams = (q: ExpensesQuery) => ({
  year: q.year,
  status: q.status,
  type: q.types,
  sponsor: q.sponsors,
  payer: q.payers,
  bexioStatus: q.bexioStatuses,
  bexioAccountId: q.bexioAccountIds,
  bexioTaxId: q.bexioTaxIds,
  createdBy: q.createdBy,
  q: q.q,
})

/**
 * The sort keys of `GET /expenses` that the expenses table offers, a subset of
 * `ExpenseRepository.sortable` (`backend/app/repositories/expense.py`): a key the backend drops is a
 * 422 for the page.
 */
export const EXPENSE_SORTABLE = ['issued', 'created', 'name', 'type', 'status', 'gross', 'sponsor', 'payer'] as const

/** `GET /expenses`'s order when the URL names none (`ExpenseRepository.default_sort`), as table sort state. */
export const EXPENSE_DEFAULT_SORTING = [{ id: 'expense_issued', desc: true }]

/**
 * The expenses table's URL filters (`f.expense_*`) as an API query, key by key: a key not listed
 * here never reaches the API, and a value the API would refuse is dropped.
 */
export const expensesQueryOf = (filters: TableFilters): ExpensesQuery => ({
  status: urlEnum(EntryStatusType, filters.expense_status),
  types: urlEnum(ExpenseType, filters.expense_type),
  sponsors: urlIds(filters.expense_sponsor),
  payers: urlIds(filters.expense_payer),
  bexioStatuses: urlEnum(ExpenseBexioStatus, filters.expense_bexio_status),
  bexioAccountIds: urlIds(filters.expense_bexio_booking_account),
  bexioTaxIds: urlIds(filters.expense_bexio_booking_tax),
})
